import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X, Settings, Check } from "lucide-react";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";

const COOKIE_CONSENT_KEY = "igoviral_cookie_consent";
const COOKIE_PREFERENCES_KEY = "igoviral_cookie_preferences";

export default function CookieConsentBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always required
    analytics: true,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already consented
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Small delay to prevent flash on page load
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
    // If consent exists, preferences are already default or will be loaded on demand
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, "all");
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(allAccepted));
    setPreferences(allAccepted);
    setShowBanner(false);
  };

  const handleAcceptNecessary = () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, "necessary");
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(necessaryOnly));
    setPreferences(necessaryOnly);
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, "custom");
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(preferences));
    setShowBanner(false);
    setShowPreferences(false);
  };

  if (!showBanner) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6"
      >
        <div className="max-w-4xl mx-auto bg-[#1A1A1B] border border-border/50 rounded-xl shadow-2xl overflow-hidden">
          {!showPreferences ? (
            // Main Banner
            <div className="p-4 md:p-6">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-[#00F0FF]/10 rounded-lg shrink-0">
                  <Cookie className="w-6 h-6 text-[#00F0FF]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    We Value Your Privacy
                  </h3>
                  <p className="text-sm text-gray-400 mb-4">
                    We use cookies to enhance your browsing experience, provide personalized content, 
                    and analyze our traffic. By clicking &quot;Accept All&quot;, you consent to our use of cookies. 
                    You can also customize your preferences.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      onClick={handleAcceptAll}
                      className="bg-gradient-to-r from-[#00F0FF] to-[#FF0055] hover:opacity-90"
                      data-testid="cookie-accept-all"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Accept All
                    </Button>
                    <Button
                      onClick={handleAcceptNecessary}
                      variant="outline"
                      className="border-border/50"
                      data-testid="cookie-accept-necessary"
                    >
                      Necessary Only
                    </Button>
                    <Button
                      onClick={() => setShowPreferences(true)}
                      variant="ghost"
                      className="text-gray-400 hover:text-white"
                      data-testid="cookie-customize"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Customize
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-3">
                    By continuing to use this site, you agree to our{" "}
                    <a href="/privacy" className="text-[#00F0FF] hover:underline">Privacy Policy</a>
                    {" "}and{" "}
                    <a href="/terms" className="text-[#00F0FF] hover:underline">Terms of Service</a>.
                  </p>
                </div>
                <button
                  onClick={handleAcceptNecessary}
                  className="p-1 text-gray-500 hover:text-white transition-colors shrink-0"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          ) : (
            // Preferences Panel
            <div className="p-4 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">Cookie Preferences</h3>
                <button
                  onClick={() => setShowPreferences(false)}
                  className="p-1 text-gray-500 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4 mb-6">
                {/* Necessary Cookies */}
                <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-white">Necessary Cookies</p>
                    <p className="text-xs text-gray-400">Required for the website to function properly</p>
                  </div>
                  <Switch checked={true} disabled className="opacity-50" />
                </div>
                
                {/* Analytics Cookies */}
                <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-white">Analytics Cookies</p>
                    <p className="text-xs text-gray-400">Help us understand how visitors use our site</p>
                  </div>
                  <Switch
                    checked={preferences.analytics}
                    onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, analytics: checked }))}
                  />
                </div>
                
                {/* Marketing Cookies */}
                <div className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-white">Marketing Cookies</p>
                    <p className="text-xs text-gray-400">Used to deliver personalized advertisements</p>
                  </div>
                  <Switch
                    checked={preferences.marketing}
                    onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, marketing: checked }))}
                  />
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button
                  onClick={handleSavePreferences}
                  className="flex-1 bg-gradient-to-r from-[#00F0FF] to-[#FF0055]"
                  data-testid="cookie-save-preferences"
                >
                  Save Preferences
                </Button>
                <Button
                  onClick={() => setShowPreferences(false)}
                  variant="outline"
                  className="border-border/50"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

// Export function to check cookie consent status
export function getCookieConsent() {
  const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
  const prefs = localStorage.getItem(COOKIE_PREFERENCES_KEY);
  
  if (!consent) return null;
  
  try {
    return prefs ? JSON.parse(prefs) : { necessary: true, analytics: false, marketing: false };
  } catch {
    return { necessary: true, analytics: false, marketing: false };
  }
}

// Export function to reset cookie consent (for testing/settings)
export function resetCookieConsent() {
  localStorage.removeItem(COOKIE_CONSENT_KEY);
  localStorage.removeItem(COOKIE_PREFERENCES_KEY);
}
